import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Bell, AlertTriangle, MapPin, Clock, DollarSign, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';

const AdminNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data);
    } catch (error) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API}/notifications/${notificationId}/read`);
      fetchNotifications();
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading notifications...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Bell className="w-8 h-8" />
            Notifications
          </h2>
          <p className="text-gray-600 mt-1">
            {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}` : 'All caught up!'}
          </p>
        </div>
      </div>

      {notifications.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center py-8 text-gray-500">No notifications yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {notifications.map((notification) => (
            <Card 
              key={notification.id} 
              className={`hover:shadow-md transition-shadow ${!notification.read ? 'border-l-4 border-l-red-500 bg-red-50' : ''}`}
            >
              <CardContent className="pt-6">
                <div className="space-y-4">
                  {/* Header */}
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-6 h-6 text-red-600" />
                      <div>
                        <h3 className="text-lg font-bold text-red-800">Driver Cancellation</h3>
                        <p className="text-sm text-gray-600">
                          {format(new Date(notification.created_at), 'MMM dd, yyyy - h:mm a')}
                        </p>
                      </div>
                    </div>
                    {!notification.read && (
                      <Badge className="bg-red-100 text-red-700">NEW</Badge>
                    )}
                  </div>

                  {/* Message */}
                  <div className="bg-gold-50 border border-gold-200 rounded-lg p-4">
                    <p className="font-semibold text-gray-800">{notification.message}</p>
                  </div>

                  {/* Ride Details */}
                  <div className="space-y-2 text-sm">
                    <div className="font-semibold text-gray-800">Ride Details:</div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <span className="font-medium">Client:</span> {notification.ride_details.client_name}
                    </div>
                    <div className="text-gray-600">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <strong>Pickup:</strong> {notification.ride_details.pickup}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <MapPin className="w-4 h-4 opacity-0" />
                        <strong>Dropoff:</strong> {notification.ride_details.dropoff}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock className="w-4 h-4" />
                      <span>{format(new Date(notification.ride_details.date), 'EEE, MMM dd, yyyy - h:mm a')}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <DollarSign className="w-4 h-4" />
                      <span>Fare: ${notification.ride_details.fare.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-4 border-t">
                    <Button
                      size="sm"
                      onClick={() => navigate('/admin/rides')}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      Manage Rides
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => navigate('/admin/drivers')}
                      variant="outline"
                    >
                      View Drivers
                    </Button>
                    {!notification.read && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => markAsRead(notification.id)}
                        className="ml-auto"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Mark as Read
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminNotifications;
